import React, { useEffect, useRef, useState } from "react";
import { api, type Me } from "./api";
import { useProblems, FieldProblem, invalid, type Rule, type Problems } from "./form-check";
import { appNow } from "./clock";
import { easternMinutes, easternToUtcMs } from "../server/station/windows";
import { stamp, DISPOSITION_LABEL } from "./Pass";
import { PersonBanner, KIND_LABEL, type PersonHeader } from "./Otc";
import { VisitEscalation } from "./VisitEscalation";
import { VisitInsurance } from "./VisitInsurance";
import { RestrictionBanner } from "./Chart";
import { CountersignAs, useTestEnvironment } from "./CountersignAs";

/**
 * Chart, Give an OTC item and Start a visit as BUTTONS beside a person's name
 * (the owner missed them as breadcrumb links, 2026-10-18): the visit page,
 * the Med station record and the Staff page use the same three.
 */
export function PersonButtons({ p, canVisit, canOtc = true, showVisit = true }: { p: PersonHeader; canVisit: boolean; canOtc?: boolean; showVisit?: boolean }) {
  const key = encodeURIComponent(p.personKey);
  return (
    <span className="flex flex-wrap gap-2">
      <a className="btn-secondary inline-flex min-h-11 items-center" href={`/chart/${key}`}>Chart</a>
      {canOtc && !p.departed && <a className="btn-secondary inline-flex min-h-11 items-center" href={`/otc/${key}`}>Give an OTC item</a>}
      {showVisit && canVisit && !p.departed && <a className="btn-secondary inline-flex min-h-11 items-center" href={`/visits/new/${key}`}>Start a visit</a>}
    </span>
  );
}

/**
 * Visits and incidents (build plan §10.1 #3; Baseline §5.3): one record for
 * injury, illness or other, students and staff. Time in/out, complaint,
 * optional vitals, assessment, treatment, disposition, follow-up; for an
 * injury, the claim form's own fields (Part I 10–14). Phone-first (375 px).
 * A save fills what is blank; changing something already recorded is a new
 * version with a reason, the original kept in the history.
 */

/** The form's vitals boxes (one reading). */
export interface Vitals {
  temp: string;
  pulse: string;
  bp: string;
  resp: string;
  spo2: string;
  takenAt: string;
}
/** One stored reading: VitalsJson is a list of these, append-only. */
export interface VitalsReading {
  takenAtUtc: string;
  by: string;
  byEmail: string;
  temp: string;
  pulse: string;
  bp: string;
  resp: string;
  spo2: string;
}
export const vitalsLine = (r: VitalsReading) => [r.temp && `T ${r.temp}`, r.pulse && `P ${r.pulse}`, r.bp && `BP ${r.bp}`, r.resp && `R ${r.resp}`, r.spo2 && `SpO₂ ${r.spo2}`].filter(Boolean).join(" · ");
const NO_VITALS: Vitals = { temp: "", pulse: "", bp: "", resp: "", spo2: "", takenAt: "" };
export interface ReferralStep {
  step: string;
  atUtc: string;
  contact: string;
  outcome: string;
}
export interface VisitView {
  visitKey: string;
  personKey: string;
  personType: string;
  personName: string;
  personDateOfBirth: string;
  kind: string;
  startedAtUtc: string;
  endedAtUtc: string;
  occurredAtUtc: string;
  location: string;
  offCampus: boolean;
  activity: string;
  narrative: string;
  bodyParts: string[];
  natureOfInjury: string;
  complaint: string;
  vitals: VitalsReading[];
  assessment: string;
  treatment: string;
  disposition: string;
  referralSteps: ReferralStep[];
  transportedTo: string;
  accompaniedBy: string;
  returnedAtUtc: string;
  supervisingAdult: string;
  supervisingTitle: string;
  supervisorWasWitness: string;
  witnesses: string;
  claim: Record<string, string>;
  outsideCare: string;
  outsideCareProvider: string;
  insuranceNoticeStatus: string;
  insuranceNoticeDueOn: string;
  directorNotifiedUtc: string;
  followUpDueOn: string;
  followUpStatus: string;
  status: string;
  recordedByEmail: string;
  recordedByName: string;
  countersignRequired: boolean;
  countersignedByEmail: string;
  /** The countersigner's display name (Stage 2: never just the email). */
  countersignedByName: string;
  countersignedAtUtc: string;
  correctsVisitKey: string;
  correctionReason: string;
  notes: string;
}
export interface VisitOptions {
  kinds: string[];
  dispositions: { code: string; label: string }[];
  bodyParts: string[];
  natures: string[];
  claimQuestions: { key: string; label: string }[];
  narrativeExample: string;
  canEdit: boolean;
  licensed: boolean;
}
export interface VisitDetail {
  person: PersonHeader;
  visit: VisitView;
  latestVisitKey: string;
  history: VisitView[];
  given: { adminKey: string; kind: string; indication: string; disposition: string; amountGiven: string; givenAtUtc: string; administeredByName: string; administeredByEmail: string }[];
  notifications: { notificationKey: string; tier: string; method: string; status: string; toName: string; subject: string; createdUtc: string }[];
  options: VisitOptions;
  canCountersign: boolean;
  escalation: { step: string; label: string; contact: string; set: boolean }[];
  directorRequired: boolean;
  canErPacket: boolean;
  voided: { byEmail: string; byName: string; atUtc: string; reason: string } | null;
  canVoid: boolean;
}

/** "Void this visit (opened in error)": RN, Supervisor, System Admin; a reason; the visit stays, marked Voided. */
function VoidVisit({ d, onSaved }: { d: VisitDetail; onSaved: (d: VisitDetail) => void }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { problems, check } = useProblems([["vd-reason", !reason.trim(), "Write why this visit is being voided (opened in error)."]]);
  if (!d.canVoid || d.latestVisitKey !== d.visit.visitKey) return null;
  async function run() {
    setError(null);
    if (!check()) return;
    setBusy(true);
    const r = await api<{ detail: VisitDetail }>("POST", `/api/visits/${encodeURIComponent(d.visit.visitKey)}/void`, { reason });
    setBusy(false);
    if (!r.ok) return setError(r.data.error ?? "Not voided.");
    onSaved(r.data.detail);
  }
  return !open ? (
    <button type="button" className="btn-secondary min-h-11" onClick={() => setOpen(true)}>Void this visit (opened in error)</button>
  ) : (
    <div className="mt-2 rounded-md border border-slate-300 bg-slate-50 p-3">
      <p className="text-sm">The visit stays on the record, marked Voided, and leaves the visit list, the reports and the countersign list. Its unsent parent notifications are withdrawn.</p>
      <label htmlFor="vd-reason" className="field-label mt-2">Why is it being voided?<span className="text-red-600"> *</span></label>
      <input id="vd-reason" className="field-input" {...invalid("vd-reason", problems)} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. started on the wrong student" />
      <FieldProblem id="vd-reason" problems={problems} />
      {error && <p className="field-error" role="alert">{error}</p>}
      <div className="mt-2 flex flex-wrap gap-2">
        <button type="button" className="btn-primary min-h-11" disabled={busy} onClick={() => void run()}>Void the visit</button>
        <button type="button" className="btn-secondary min-h-11" onClick={() => setOpen(false)}>Cancel</button>
      </div>
    </div>
  );
}

export const STEP_LABEL: Record<string, string> = { CampusResponders: "Campus first responders", Call911: "911 (then campus security)", LocalEms: "Local EMS", RemoteProvider: "Remote provider", UrgentCare: "Urgent care", ER: "Emergency room" };

/** "HH:MM" in New York for an instant (the form's times). */
export function hhmmOf(iso: string): string {
  if (!iso) return "";
  const m = easternMinutes(Date.parse(iso));
  return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
}
const nowHHMM = () => hhmmOf(new Date(appNow()).toISOString());

interface Form {
  kind: string;
  startedAt: string;
  complaint: string;
  location: string;
  offCampus: boolean;
  activity: string;
  occurredAt: string;
  narrative: string;
  bodyParts: string[];
  natureOfInjury: string;
  vitals: Vitals;
  assessment: string;
  treatment: string;
  disposition: string;
  supervisingAdult: string;
  supervisingTitle: string;
  supervisorWasWitness: string;
  witnesses: string;
  claim: Record<string, string>;
  outsideCare: string;
  outsideCareProvider: string;
  followUpDueOn: string;
  followUpStatus: string;
  endedAt: string;
  notes: string;
  correctionReason: string;
}

const formOf = (v: VisitView | null, complaint = ""): Form => ({
  kind: v?.kind ?? "Illness",
  startedAt: v ? hhmmOf(v.startedAtUtc) : nowHHMM(),
  complaint: v?.complaint ?? complaint,
  location: v?.location ?? "",
  offCampus: v?.offCampus ?? false,
  activity: v?.activity ?? "",
  occurredAt: v ? hhmmOf(v.occurredAtUtc) : "",
  narrative: v?.narrative ?? "",
  bodyParts: v?.bodyParts ?? [],
  natureOfInjury: v?.natureOfInjury ?? "",
  // A new visit's first reading; on a saved visit readings are added with "Add vitals", never edited.
  vitals: NO_VITALS,
  assessment: v?.assessment ?? "",
  treatment: v?.treatment ?? "",
  disposition: v?.disposition ?? "",
  supervisingAdult: v?.supervisingAdult ?? "",
  supervisingTitle: v?.supervisingTitle ?? "",
  supervisorWasWitness: v?.supervisorWasWitness ?? "",
  witnesses: v?.witnesses ?? "",
  claim: v?.claim ?? {},
  outsideCare: v?.outsideCare ?? "",
  outsideCareProvider: v?.outsideCareProvider ?? "",
  followUpDueOn: v?.followUpDueOn ?? "",
  followUpStatus: v?.followUpStatus ?? "",
  endedAt: v ? hhmmOf(v.endedAtUtc) : "",
  notes: v?.notes ?? "",
  correctionReason: "",
});

/** The fields that, once recorded, only change as a new version (with a reason). */
const WRITE_ONCE: (keyof Form)[] = ["kind", "startedAt", "complaint", "location", "activity", "occurredAt", "narrative", "natureOfInjury", "assessment", "treatment", "disposition", "supervisingAdult", "supervisingTitle", "supervisorWasWitness", "witnesses", "endedAt", "notes"];
export function rewritten(orig: Form, f: Form): string[] {
  // As the server compares: trimmed, so a trailing space is not a change to what is recorded.
  const out: string[] = WRITE_ONCE.filter((k) => String(orig[k] ?? "").trim() !== "" && String(orig[k]).trim() !== String(f[k] ?? "").trim());
  if (orig.bodyParts.length && orig.bodyParts.join(",") !== f.bodyParts.join(",")) out.push("bodyParts");
  for (const k of Object.keys(orig.claim)) if (orig.claim[k] && orig.claim[k] !== f.claim[k]) out.push(k);
  return out;
}

/** The visit form's rules; `closing` adds what closing needs (an injury's claim-form fields, §10.4 #5). */
export function visitRules(f: Form, o: VisitOptions, closing: boolean, needsReason: boolean): Rule[] {
  const injury = f.kind === "Injury";
  return [
    ["v-complaint", !f.complaint.trim(), "Write the complaint."],
    ["v-reason", needsReason && !f.correctionReason.trim(), "Write why the recorded visit is being changed."],
    ["v-disp", closing && !f.disposition, "This visit can't close yet: it needs the disposition."],
    ["v-occ", closing && injury && !f.occurredAt, "An injury needs when it happened."],
    ["v-loc", closing && injury && !f.location.trim(), "An injury needs where it happened."],
    ["v-narr", closing && injury && !f.narrative.trim(), "An injury needs how it happened."],
    ["v-parts", closing && injury && !f.bodyParts.length, "An injury needs the body part(s)."],
    ["v-nature", closing && injury && !f.natureOfInjury.trim(), "An injury needs the nature of the injury."],
    ...o.claimQuestions.map((q): Rule => [`v-claim-${q.key}`, closing && injury && !f.claim[q.key.charAt(0).toUpperCase() + q.key.slice(1)], `An injury needs “${q.label}”.`]),
    ["v-sup", closing && injury && !f.supervisingAdult.trim(), "An injury needs the supervising adult."],
    ["v-wit", closing && injury && !f.witnesses.trim(), "An injury needs the witnesses (or “none”)."],
  ];
}

/** The five vitals and the time taken: one reading. */
function VitalsBoxes({ v, onChange, idPrefix = "v-vit" }: { v: Vitals; onChange: (v: Vitals) => void; idPrefix?: string }) {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
      {([["temp", "Temp"], ["pulse", "Pulse"], ["bp", "BP"], ["resp", "Resp"], ["spo2", "SpO₂"]] as const).map(([k, label]) => (
        <div key={k}><label htmlFor={`${idPrefix}-${k}`} className="field-label">{label}</label><input id={`${idPrefix}-${k}`} className="field-input" inputMode={k === "bp" ? "text" : "decimal"} value={v[k]} onChange={(e) => onChange({ ...v, [k]: e.target.value })} /></div>
      ))}
      <div><label htmlFor={`${idPrefix}-at`} className="field-label">Taken at</label><input id={`${idPrefix}-at`} type="time" className="field-input" value={v.takenAt} onChange={(e) => onChange({ ...v, takenAt: e.target.value })} /></div>
    </div>
  );
}

/**
 * The visit's vitals: every reading as taken, oldest first, with when and by
 * whom; "Add vitals" adds one (append-only: a reading is never edited).
 */
export function VitalsList({ d, onSaved }: { d: VisitDetail; onSaved: (d: VisitDetail) => void }) {
  const v = d.visit;
  const editable = d.latestVisitKey === v.visitKey && d.options.canEdit && !d.voided;
  const [open, setOpen] = useState(false);
  const [r, setR] = useState<Vitals>({ ...NO_VITALS, takenAt: nowHHMM() });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { problems, check, reset } = useProblems([["vt-temp", ![r.temp, r.pulse, r.bp, r.resp, r.spo2].some((x) => x.trim()), "Enter at least one of the vitals."]]);
  async function add() {
    setError(null);
    if (!check()) return;
    setBusy(true);
    const day = new Intl.DateTimeFormat("en-CA", { timeZone: "America/New_York" }).format(new Date(appNow()));
    const x = await api<{ detail: VisitDetail }>("POST", `/api/visits/${encodeURIComponent(v.visitKey)}/vitals`, { ...r, takenAt: r.takenAt ? new Date(easternToUtcMs(day, r.takenAt)).toISOString() : "" });
    setBusy(false);
    if (!x.ok) return setError(x.data.error ?? "Not added.");
    setR({ ...NO_VITALS, takenAt: nowHHMM() });
    reset();
    setOpen(false);
    onSaved(x.data.detail);
  }
  return (
    <section className="section-card">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-base font-semibold">Vitals</h3>
        {editable && !open && <button type="button" className="btn-secondary min-h-11" onClick={() => setOpen(true)}>Add vitals</button>}
      </div>
      {v.vitals.length === 0 ? <p className="text-sm text-slate-600">None taken.</p> : (
        <ol className="mt-1 space-y-1 text-sm">
          {v.vitals.map((x, i) => <li key={i}><span className="text-slate-500">{x.takenAtUtc ? stamp(x.takenAtUtc) : "time not given"}</span> · {vitalsLine(x)}{x.by ? <span className="text-slate-500"> · {x.by}</span> : null}</li>)}
        </ol>
      )}
      {open && (
        <div className="mt-2 rounded-md border border-slate-200 p-3">
          <VitalsBoxes v={r} onChange={setR} idPrefix="vt" />
          <FieldProblem id="vt-temp" problems={problems} />
          {error && <p className="field-error" role="alert">{error}</p>}
          <div className="mt-2 flex flex-wrap gap-2">
            <button type="button" className="btn-primary min-h-11" disabled={busy} onClick={() => void add()}>Add the reading</button>
            <button type="button" className="btn-secondary min-h-11" onClick={() => { setOpen(false); reset(); }}>Cancel</button>
          </div>
        </div>
      )}
    </section>
  );
}

function YesNo({ id, value, onChange, problems }: { id: string; value: string; onChange: (v: string) => void; problems: Problems }) {
  return (
    <div id={id} className="flex gap-2" role="radiogroup">
      {["Y", "N"].map((x) => (
        <button key={x} type="button" role="radio" aria-checked={value === x} className={`min-h-11 min-w-16 rounded-md border px-3 text-sm ${value === x ? "border-program bg-program text-white" : problems[id] ? "border-red-500 bg-white" : "border-slate-300 bg-white"}`} onClick={() => onChange(value === x ? "" : x)}>
          {x === "Y" ? "Yes" : "No"}
        </button>
      ))}
    </div>
  );
}

export function VisitForm({ personKey, original, options, startComplaint, onSaved, onCancel, flash }: { personKey: string; original: VisitView | null; options: VisitOptions; startComplaint?: string; onSaved: (d: VisitDetail, closed: boolean) => void; onCancel?: () => void; flash?: string | null }) {
  const orig = formOf(original, startComplaint);
  const [f, setF] = useState<Form>(orig);
  const [closing, setClosing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** The server found a change to what is recorded that the form did not: ask why (its message names the fields). */
  const [askWhy, setAskWhy] = useState<string | null>(null);
  const set = (p: Partial<Form>) => setF({ ...f, ...p });
  const changedRecorded = original ? rewritten(orig, f) : [];
  const needsReason = changedRecorded.length > 0 || Boolean(askWhy);
  const rulesFor = (c: boolean) => visitRules(f, options, c, needsReason);
  const { problems, check } = useProblems(rulesFor(closing));
  const P = ({ k }: { k: string }) => <FieldProblem id={k} problems={problems} />;
  const injury = f.kind === "Injury";
  /** How many claim-form fields are still blank (the close rule's own list). */
  const claimMissing = injury ? visitRules(f, options, true, false).filter(([id, failing]) => failing && id !== "v-complaint" && id !== "v-disp" && id !== "v-reason").length : 0;
  async function save(close: boolean) {
    setClosing(close);
    setError(null);
    if (!check(rulesFor(close))) return;
    setBusy(true);
    // Times are instants on the visit's own day (a visit from yesterday keeps its date).
    const day = new Intl.DateTimeFormat("en-CA", { timeZone: "America/New_York" }).format(new Date(original ? Date.parse(original.startedAtUtc) : appNow()));
    const at = (k: "startedAt" | "endedAt" | "occurredAt") => (original && f[k] === orig[k] ? undefined : f[k] ? new Date(easternToUtcMs(day, f[k])).toISOString() : "");
    const times = { startedAt: at("startedAt"), endedAt: at("endedAt"), occurredAt: at("occurredAt"), vitals: original ? undefined : { ...f.vitals, takenAt: /^\d{2}:\d{2}$/.test(f.vitals.takenAt) ? new Date(easternToUtcMs(day, f.vitals.takenAt)).toISOString() : f.vitals.takenAt } };
    const body = { ...f, ...times, ...(original ? {} : { personKey }), claim: undefined, ...Object.fromEntries(options.claimQuestions.map((q) => [q.key, f.claim[q.key.charAt(0).toUpperCase() + q.key.slice(1)] ?? ""])), close };
    const r = original ? await api<{ detail: VisitDetail }>("PUT", `/api/visits/${encodeURIComponent(original.visitKey)}`, body) : await api<{ detail: VisitDetail }>("POST", "/api/visits", body);
    setBusy(false);
    if (!r.ok) {
      if (r.data.code === "correctionReasonRequired") setAskWhy(r.data.error ?? "Write why the recorded visit is being changed.");
      return setError(r.data.error ?? "Not saved.");
    }
    onSaved(r.data.detail, close);
  }
  const text = (k: keyof Form, id: string, label: string, opts: { area?: boolean; placeholder?: string; required?: boolean } = {}) => (
    <div>
      <label htmlFor={id} className="field-label">{label}{opts.required && <span className="text-red-600"> *</span>}</label>
      {opts.area ? (
        <textarea id={id} className="field-input min-h-20" {...invalid(id, problems)} value={String(f[k] ?? "")} placeholder={opts.placeholder} onChange={(e) => set({ [k]: e.target.value } as Partial<Form>)} />
      ) : (
        <input id={id} className="field-input" {...invalid(id, problems)} value={String(f[k] ?? "")} placeholder={opts.placeholder} onChange={(e) => set({ [k]: e.target.value } as Partial<Form>)} />
      )}
      <P k={id} />
    </div>
  );
  return (
    <div className="space-y-4">
      <section className="section-card space-y-3">
        <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Kind">
          {options.kinds.map((k) => (
            <button key={k} type="button" role="radio" aria-checked={f.kind === k} className={`min-h-11 rounded-md border px-4 text-sm font-medium ${f.kind === k ? "border-program bg-program text-white" : "border-slate-300 bg-white"}`} onClick={() => set({ kind: k })}>{KIND_LABEL[k] ?? k}</button>
          ))}
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div><label htmlFor="v-in" className="field-label">Time in</label><input id="v-in" type="time" className="field-input" value={f.startedAt} onChange={(e) => set({ startedAt: e.target.value })} /></div>
          <div><label htmlFor="v-out" className="field-label">Time out</label><input id="v-out" type="time" className="field-input" value={f.endedAt} onChange={(e) => set({ endedAt: e.target.value })} /></div>
        </div>
        {text("complaint", "v-complaint", "Complaint", { area: true, required: true })}
        <div className="grid gap-3 sm:grid-cols-2">
          {text("location", "v-loc", injury ? "Where it happened (place)" : "Place", { required: injury, placeholder: "Med station, gym, field trip site…" })}
          {text("activity", "v-act", "Activity at the time")}
        </div>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.offCampus} onChange={(e) => set({ offCampus: e.target.checked })} />Off campus</label>
      </section>

      {!original && (
        <section className="section-card">
          <h3 className="mb-2 text-base font-semibold">Vitals (as taken; all optional)</h3>
          <VitalsBoxes v={f.vitals} onChange={(vitals) => set({ vitals })} />
        </section>
      )}

      {injury && (
        <details className="section-card border-l-4 border-l-amber-500" open={claimMissing > 0 || closing || undefined}>
          <summary className="flex min-h-11 cursor-pointer flex-wrap items-center justify-between gap-x-3">
            <h3 className="text-base font-semibold">Injury: the claim form's fields</h3>
            <span className={`text-sm ${claimMissing ? "font-medium text-amber-900" : "text-slate-600"}`}>{claimMissing ? `${claimMissing} still to fill before the visit closes` : "Complete"}</span>
          </summary>
          <div className="mt-3 space-y-3">
          <div><label htmlFor="v-occ" className="field-label">When it happened<span className="text-red-600"> *</span></label><input id="v-occ" type="time" className="field-input max-w-[12rem]" {...invalid("v-occ", problems)} value={f.occurredAt} onChange={(e) => set({ occurredAt: e.target.value })} /><P k="v-occ" /></div>
          <div>
            <label htmlFor="v-narr" className="field-label">How it happened<span className="text-red-600"> *</span></label>
            <p className="text-xs text-slate-600">Be specific: {options.narrativeExample}</p>
            <textarea id="v-narr" className="field-input min-h-20" {...invalid("v-narr", problems)} value={f.narrative} onChange={(e) => set({ narrative: e.target.value })} />
            <P k="v-narr" />
          </div>
          <div id="v-parts">
            <p className="field-label">Body part(s)<span className="text-red-600"> *</span></p>
            <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-sm sm:grid-cols-4">
              {options.bodyParts.map((b) => (
                <label key={b} className="flex items-center gap-1.5"><input type="checkbox" checked={f.bodyParts.includes(b)} onChange={() => set({ bodyParts: f.bodyParts.includes(b) ? f.bodyParts.filter((x) => x !== b) : [...f.bodyParts, b] })} />{b}</label>
              ))}
            </div>
            <input aria-label="Other body part, side" className="field-input mt-1" placeholder="Side, or another part (e.g. left)" value={f.bodyParts.filter((b) => !options.bodyParts.includes(b)).join(", ")} onChange={(e) => set({ bodyParts: [...f.bodyParts.filter((b) => options.bodyParts.includes(b)), ...e.target.value.split(",").map((x) => x.trim()).filter(Boolean)] })} />
            <P k="v-parts" />
          </div>
          <div>
            <label htmlFor="v-nature" className="field-label">Nature of the injury<span className="text-red-600"> *</span></label>
            <input id="v-nature" className="field-input" list="v-natures" {...invalid("v-nature", problems)} value={f.natureOfInjury} onChange={(e) => set({ natureOfInjury: e.target.value })} />
            <datalist id="v-natures">{options.natures.map((n) => <option key={n} value={n} />)}</datalist>
            <P k="v-nature" />
          </div>
          <div className="space-y-2">
            {options.claimQuestions.map((q) => {
              const col = q.key.charAt(0).toUpperCase() + q.key.slice(1);
              return (
                <div key={q.key} className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-sm">{q.label}<span className="text-red-600"> *</span></span>
                  <YesNo id={`v-claim-${q.key}`} value={f.claim[col] ?? ""} onChange={(x) => set({ claim: { ...f.claim, [col]: x } })} problems={problems} />
                  <div className="w-full"><P k={`v-claim-${q.key}`} /></div>
                </div>
              );
            })}
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {text("supervisingAdult", "v-sup", "Supervising adult", { required: true })}
            {text("supervisingTitle", "v-supt", "Their title")}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2"><span className="text-sm">Did the supervising adult see it happen?</span><YesNo id="v-supw" value={f.supervisorWasWitness} onChange={(x) => set({ supervisorWasWitness: x })} problems={problems} /></div>
          {text("witnesses", "v-wit", "Other witnesses (or “none”)", { required: true })}
          </div>
        </details>
      )}

      <section className="section-card space-y-3">
        {text("assessment", "v-assess", "Assessment", { area: true })}
        {text("treatment", "v-treat", "Treatment given", { area: true, placeholder: "OTC given here is recorded with “Give an OTC item”." })}
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor="v-disp" className="field-label">Disposition</label>
            <select id="v-disp" className="field-input min-h-11" {...invalid("v-disp", problems)} value={f.disposition} onChange={(e) => set({ disposition: e.target.value })}>
              <option value="">—</option>
              {options.dispositions.map((d) => <option key={d.code} value={d.code}>{d.label}</option>)}
            </select>
            <P k="v-disp" />
          </div>
          <div>
            <label htmlFor="v-fu" className="field-label">Follow-up due</label>
            <input id="v-fu" type="date" className="field-input" value={f.followUpDueOn} onChange={(e) => set({ followUpDueOn: e.target.value, followUpStatus: e.target.value && !f.followUpStatus ? "Open" : f.followUpStatus })} />
            {f.followUpDueOn && <label className="mt-1 flex items-center gap-2 text-sm"><input type="checkbox" checked={f.followUpStatus === "Done"} onChange={(e) => set({ followUpStatus: e.target.checked ? "Done" : "Open" })} />Follow-up done</label>}
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2"><span className="text-sm">Outside care (urgent care, ER, a physician)?</span><YesNo id="v-oc" value={f.outsideCare} onChange={(x) => set({ outsideCare: x })} problems={problems} /></div>
        {f.outsideCare === "Y" && text("outsideCareProvider", "v-ocp", "Where (provider)")}
        {text("notes", "v-notes", "Notes", { area: true })}
        {needsReason && (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3">
            <p className="text-sm text-amber-950">You changed something already recorded: this saves a new version; the original stays in the history.</p>
            {text("correctionReason", "v-reason", "Why the recorded visit is being changed", { required: true })}
          </div>
        )}
      </section>
      {error && <p className="field-error" role="alert">{error}</p>}
      <div className="flex flex-wrap gap-2">
        <button type="button" className="btn-primary min-h-11" disabled={busy} onClick={() => void save(false)}>{original ? "Save" : "Start the visit"}</button>
        <button type="button" className="btn-secondary min-h-11" disabled={busy} onClick={() => void save(true)}>{original ? "Save and close the visit" : "Save and close"}</button>
        {onCancel && <button type="button" className="btn-secondary min-h-11" onClick={onCancel}>Cancel</button>}
        {flash && <span className="self-center text-sm font-medium text-emerald-800" role="status">{flash}</span>}
      </div>
    </div>
  );
}

function VisitSummary({ v }: { v: VisitView }) {
  return (
    <div className="text-sm">
      <p><span className="font-semibold">{KIND_LABEL[v.kind] ?? v.kind}</span> · in {stamp(v.startedAtUtc)}{v.endedAtUtc ? ` · out ${stamp(v.endedAtUtc)}` : ""} · {v.location || "place not given"}{v.offCampus ? " (off campus)" : ""}</p>
      <p>Complaint: {v.complaint}</p>
      {v.vitals.map((x, i) => <p key={i}>Vitals{x.takenAtUtc ? ` ${stamp(x.takenAtUtc)}` : ""}: {vitalsLine(x)}</p>)}
      {v.assessment && <p>Assessment: {v.assessment}</p>}
      {v.treatment && <p>Treatment: {v.treatment}</p>}
      {v.disposition && <p>Disposition: {v.disposition}</p>}
      <p className="text-xs text-slate-500">Recorded by {v.recordedByName || v.recordedByEmail}{v.correctionReason ? ` · changed: ${v.correctionReason}` : ""}</p>
    </div>
  );
}

function VisitScreen({ visitKey, me }: { visitKey: string; me: Me }) {
  const [d, setD] = useState<VisitDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [csError, setCsError] = useState<string | null>(null);
  /** Bumped on every save: the form remounts on what was saved, so a filled blank becomes the new baseline. */
  const [rev, setRev] = useState(0);
  const [flash, setFlash] = useState<string | null>(null);
  const isTest = useTestEnvironment();
  const load = (key = visitKey) => void api<{ detail: VisitDetail }>("GET", `/api/visits/${encodeURIComponent(key)}`).then((r) => (r.ok ? setD(r.data.detail) : setError(r.data.error ?? "Could not load.")));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => load(), [visitKey]);
  if (error) return <div className="section-card" role="alert"><p className="text-sm text-red-700">{error}</p></div>;
  if (!d) return <p className="text-sm text-slate-600">Loading…</p>;
  const v = d.visit;
  const latest = d.latestVisitKey === v.visitKey;
  const saved = (nd: VisitDetail, closed = false) => {
    setD(nd);
    setRev((n) => n + 1);
    setFlash(closed && nd.visit.status === "Closed" ? "Visit closed" : nd.visit.visitKey !== v.visitKey ? "Saved as a new version" : "Saved");
    if (nd.visit.visitKey !== visitKey) window.history.replaceState(null, "", `/visits/${encodeURIComponent(nd.visit.visitKey)}`);
  };
  async function countersign() {
    setCsError(null);
    const r = await api<{ detail: VisitDetail }>("POST", `/api/visits/${encodeURIComponent(v.visitKey)}/countersign`, {});
    if (r.ok) setD(r.data.detail);
    else setCsError(r.data.error ?? "Not countersigned.");
  }
  return (
    <div>
      <p className="mb-3 text-sm"><a href="/visits" className="text-program underline">← Visits</a></p>
      <PersonBanner p={d.person} />
      <RestrictionBanner r={d.person.restriction} />
      <section className="section-card">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-xl font-semibold">Visit: {d.person.lastName}, {d.person.firstName}</h2>
          <PersonButtons p={d.person} canVisit={d.options.canEdit} />
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-2 text-sm">
          <span className={`rounded px-2 py-0.5 text-xs font-semibold ${v.status === "Open" ? "bg-amber-100 text-amber-950" : "bg-slate-200 text-slate-800"}`}>{v.status}</span>
          {v.countersignRequired && <span className="rounded bg-amber-200 px-2 py-0.5 text-xs font-medium text-amber-950">unverified: to countersign</span>}
          {v.countersignedByEmail && <span className="text-xs text-slate-600">countersigned by {v.countersignedByName || v.countersignedByEmail}</span>}
          {flash && <span className="font-medium text-emerald-800" role="status">{flash}</span>}
        </div>
        <p className="text-sm text-slate-600">{d.person.personKey}{d.person.dateOfBirth ? ` · born ${d.person.dateOfBirth}` : ""}{d.person.personType === "Staff" ? " · staff" : ` · ${d.person.city || "Not assigned"}`}</p>
        {v.insuranceNoticeStatus === "Due" && <p className="mt-1 text-sm font-semibold text-amber-900">Insurance notice due by {v.insuranceNoticeDueOn} (an injury with outside care).</p>}
        {v.insuranceNoticeStatus === "Sent" && <p className="mt-1 text-sm text-emerald-800">Insurance notice sent.</p>}
        {!latest && <p className="mt-1 text-sm font-medium text-amber-900">An earlier version. <a className="underline" href={`/visits/${encodeURIComponent(d.latestVisitKey)}`}>Open the latest</a>.</p>}
        {d.history.length > 0 && (
          <p className="mt-1 text-sm">
            <button type="button" className="text-program underline" onClick={() => setHistoryOpen(!historyOpen)}>corrected — see history ({d.history.length})</button>
          </p>
        )}
        {historyOpen && <ol className="mt-2 list-decimal space-y-2 pl-5">{d.history.map((h) => <li key={h.visitKey}><VisitSummary v={h} /></li>)}</ol>}
        {d.voided && (
          <p className="mt-2 rounded-md border border-slate-400 bg-slate-100 p-2 text-sm text-slate-800" role="note">
            <span className="font-semibold">Voided (opened in error)</span>{d.voided.atUtc ? ` · ${stamp(d.voided.atUtc)}` : ""}{d.voided.byName || d.voided.byEmail ? ` · by ${d.voided.byName || d.voided.byEmail}` : ""}{d.voided.reason ? `: ${d.voided.reason}` : ""}. Kept on the record; it counts nowhere.
          </p>
        )}
        {d.canCountersign && (
          <div className="mt-2">
            <button type="button" className="btn-secondary min-h-11" onClick={() => void countersign()}>Countersign this visit</button>
            {csError && <p className="field-error" role="alert">{csError}</p>}
          </div>
        )}
        {isTest && v.countersignRequired && <CountersignAs path={`/api/test/visits/${encodeURIComponent(v.visitKey)}/countersign-as`} onDone={() => load()} />}
        <div className="mt-2"><VoidVisit d={d} onSaved={(nd) => saved(nd)} /></div>
      </section>
      {d.given.length > 0 && (
        <section className="section-card">
          <h3 className="mb-1 text-base font-semibold">Given during this visit</h3>
          <ul className="text-sm">{d.given.map((a) => <li key={a.adminKey}>{a.kind} · {DISPOSITION_LABEL[a.disposition] ?? a.disposition} · {a.amountGiven} · {stamp(a.givenAtUtc)} · {a.administeredByName || a.administeredByEmail}{a.indication ? ` · for ${a.indication}` : ""}</li>)}</ul>
        </section>
      )}
      {/* One block, bound to the version shown (a new version remounts it). The keys differ from the form's:
          siblings sharing a key left stale copies of the block on the page, one per save. */}
      <VitalsList key={`vitals-${v.visitKey}`} d={d} onSaved={(nd) => saved(nd)} />
      {latest && d.options.canEdit && !d.voided ? (
        <VisitForm key={`form-${v.visitKey}-${rev}`} personKey={d.person.personKey} original={v} options={d.options} onSaved={saved} flash={flash} />
      ) : (
        <section className="section-card"><VisitSummary v={v} /></section>
      )}
      <VisitEscalation key={`escalation-${v.visitKey}`} d={d} onSaved={(nd) => saved(nd)} />
      {v.kind === "Injury" && !d.voided && <VisitInsurance key={`insurance-${v.visitKey}-${rev}`} visitKey={v.visitKey} me={me} />}
      {d.notifications.length > 0 && (
        <section className="section-card">
          <h3 className="mb-1 text-base font-semibold">Parent notification</h3>
          <ul className="text-sm">{d.notifications.map((n) => <li key={n.notificationKey}>{n.tier} ({n.method}) · {n.status} · {n.toName || "—"} · drafted {stamp(n.createdUtc)}</li>)}</ul>
          <p className="mt-1 text-sm"><a className="text-program underline" href="/reports/17">Open the notification queue (#17)</a>: emails are released and sent there; calls are logged there or under Parent contact.</p>
        </section>
      )}
      <p className="sr-only">{me.name}</p>
    </div>
  );
}

function NewVisit({ personKey, complaint }: { personKey: string; complaint: string }) {
  const [person, setPerson] = useState<PersonHeader | null>(null);
  const [options, setOptions] = useState<VisitOptions | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    void api<{ person: PersonHeader }>("GET", `/api/otc/${encodeURIComponent(personKey)}`).then((r) => (r.ok ? setPerson(r.data.person) : setError(r.data.error ?? "Could not load.")));
    void api<{ options: VisitOptions }>("GET", "/api/visits").then((r) => r.ok && setOptions(r.data.options));
  }, [personKey]);
  if (error) return <div className="section-card" role="alert"><p className="text-sm text-red-700">{error}</p></div>;
  if (!person || !options) return <p className="text-sm text-slate-600">Loading…</p>;
  return (
    <div>
      <p className="mb-3 text-sm"><a href="/visits" className="text-program underline">← Visits</a></p>
      <PersonBanner p={person} />
      <h2 className="mb-2 text-xl font-semibold">New visit: {person.lastName}, {person.firstName}</h2>
      <VisitForm personKey={personKey} original={null} options={options} startComplaint={complaint} onSaved={(d) => window.location.assign(`/visits/${encodeURIComponent(d.visit.visitKey)}`)} />
    </div>
  );
}

function VisitList() {
  const [data, setData] = useState<{ today: string; day: string; all: boolean; visits: VisitView[]; options: VisitOptions } | null>(null);
  const [q, setQ] = useState("");
  const [found, setFound] = useState<{ personKey: string; name: string; note: string }[]>([]);
  /** "" = today and every open visit (the default); a date = that day; "all" = every visit this session. */
  const [day, setDay] = useState("");
  const [showVoided, setShowVoided] = useState(false);
  const seq = useRef(0);
  useEffect(() => {
    const qs = [day === "all" ? "all=1" : day ? `day=${day}` : "", showVoided ? "voided=1" : ""].filter(Boolean).join("&");
    void api<{ today: string; day: string; all: boolean; visits: VisitView[]; options: VisitOptions }>("GET", `/api/visits${qs ? `?${qs}` : ""}`).then((r) => r.ok && setData(r.data));
  }, [day, showVoided]);
  useEffect(() => {
    const term = q.trim();
    if (!term) return setFound([]);
    const mine = ++seq.current;
    const t = setTimeout(async () => {
      const r = await api<{ people: { personKey: string; lastName: string; firstName: string; city: string }[]; staff: { personKey: string; lastName: string; firstName: string }[] }>("GET", `/api/station/search?q=${encodeURIComponent(term)}`);
      if (mine !== seq.current || !r.ok) return;
      setFound([...r.data.people.map((p) => ({ personKey: p.personKey, name: `${p.lastName}, ${p.firstName}`, note: p.city || "Not assigned" })), ...(r.data.staff ?? []).map((s) => ({ personKey: s.personKey, name: `${s.lastName}, ${s.firstName}`, note: "staff" }))]);
    }, 250);
    return () => clearTimeout(t);
  }, [q]);
  if (!data) return <p className="text-sm text-slate-600">Loading…</p>;
  return (
    <section>
      <h2 className="mb-1 text-xl font-semibold">Visits</h2>
      <p className="mb-3 text-sm text-slate-600">Injury, illness or other, for students and staff.</p>
      {data.options.canEdit && (
        <div className="section-card">
          <label htmlFor="vs-q" className="field-label">Start a visit: find the person</label>
          <input id="vs-q" className="field-input w-full" value={q} onChange={(e) => setQ(e.target.value)} autoComplete="off" placeholder="Name, date of birth or Reference Number" />
          {found.length > 0 && (
            <ul className="mt-2 divide-y divide-slate-100">
              {found.map((p) => <li key={p.personKey} className="py-2 text-sm"><a className="font-medium text-program underline" href={`/visits/new/${encodeURIComponent(p.personKey)}`}>{p.name}</a> <span className="text-slate-500">{p.personKey} · {p.note}</span></li>)}
            </ul>
          )}
        </div>
      )}
      <div className="section-card">
        <div className="mb-2 flex flex-wrap items-end gap-3">
          <div>
            <label htmlFor="vs-day" className="field-label">Day</label>
            <input id="vs-day" type="date" className="field-input min-h-11" value={day && day !== "all" ? day : ""} onChange={(e) => setDay(e.target.value)} />
          </div>
          <button type="button" className={`min-h-11 rounded-md border px-3 text-sm ${day === "" ? "border-program bg-program text-white" : "border-slate-300 bg-white"}`} onClick={() => setDay("")}>Today and open</button>
          <button type="button" className={`min-h-11 rounded-md border px-3 text-sm ${day === "all" ? "border-program bg-program text-white" : "border-slate-300 bg-white"}`} onClick={() => setDay("all")}>All visits this session</button>
          <label className="flex min-h-11 items-center gap-2 text-sm"><input type="checkbox" checked={showVoided} onChange={(e) => setShowVoided(e.target.checked)} />Show voided</label>
        </div>
        <p className="mb-1 text-xs text-slate-500">{day === "all" ? "Every visit this session" : day ? `Visits on ${day}` : "Today's visits and every visit still open"}{showVoided ? ", voided included" : ""}.</p>
        {data.visits.length === 0 ? <p className="text-sm text-slate-600">{day === "" ? "No visits today and none open." : "No visits."}</p> : (
          <ul className="divide-y divide-slate-100">
            {data.visits.map((v) => (
              <li key={v.visitKey} className="py-2 text-sm">
                <a className="font-medium text-program underline" href={`/visits/${encodeURIComponent(v.visitKey)}`}>{v.personName}</a>
                <span className="ml-2 text-slate-600">{KIND_LABEL[v.kind] ?? v.kind} · {stamp(v.startedAtUtc)} · {v.complaint}</span>
                <span className="ml-2 inline-flex flex-wrap gap-1">
                  <span className={`rounded px-1.5 text-xs font-semibold ${v.status === "Open" ? "bg-amber-100 text-amber-950" : v.status === "Voided" ? "bg-slate-100 text-slate-500 line-through" : "bg-slate-200 text-slate-800"}`}>{v.status}</span>
                  {v.personType === "Staff" && <span className="rounded bg-sky-100 px-1.5 text-xs text-sky-900">staff</span>}
                  {v.countersignRequired && <span className="rounded bg-amber-200 px-1.5 text-xs">to countersign</span>}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

export function Visits({ path, me }: { path: string; me: Me }): React.ReactElement {
  if (path.startsWith("/visits/new/")) {
    const key = decodeURIComponent(path.slice("/visits/new/".length));
    const complaint = new URLSearchParams(window.location.search).get("complaint") ?? "";
    return <NewVisit key={key} personKey={key} complaint={complaint} />;
  }
  if (path.startsWith("/visits/")) return <VisitScreen key={path} visitKey={decodeURIComponent(path.slice("/visits/".length))} me={me} />;
  return <VisitList />;
}
